import { useState, useEffect } from 'react';
import {
  Video,
  Volume2,
  ChevronLeft,
  ChevronRight,
  Settings,
  Menu,
  X,
  ImageIcon,
  Share2,
  Scissors,
  LayoutGrid,
} from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { publicAsset } from '../../utils/assetUrl';
import { useGeneration } from '../../contexts/GenerationContext.context';
import { SidebarBadge } from './SidebarBadge';
import type { GenerationTabMode } from '../../types/generationJob';

export type AppMode =
  | 'video'
  | 'image'
  | 'audio'
  | 'youtube'
  | 'videoEditor'
  | 'misc'
  | 'settings'
  // Route-only (reachable via deep-links / CTAs, not shown in the sidebar):
  | 'download';

interface SidebarItem {
  id: AppMode;
  icon: React.ReactNode;
  labelKey: string;
  defaultLabel: string;
  testId: string;
}

interface SidebarProps {
  currentMode: AppMode;
  onModeChange: (mode: AppMode) => void;
}

const sidebarItems: SidebarItem[] = [
  {
    id: 'video',
    icon: <Video className="h-5 w-5" />,
    labelKey: 'app.header.videoTab',
    defaultLabel: 'Video',
    testId: 'video-tab',
  },
  {
    id: 'image',
    icon: <ImageIcon className="h-5 w-5" />,
    labelKey: 'app.header.imageTab',
    defaultLabel: 'Image',
    testId: 'image-tab',
  },
  {
    id: 'audio',
    icon: <Volume2 className="h-5 w-5" />,
    labelKey: 'app.header.audioTab',
    defaultLabel: 'Audio',
    testId: 'audio-tab',
  },
  {
    id: 'youtube',
    icon: <Share2 className="h-5 w-5" />,
    labelKey: 'app.header.youtubeTab',
    defaultLabel: 'Social',
    testId: 'youtube-tab',
  },
  {
    id: 'videoEditor',
    icon: <Scissors className="h-5 w-5" />,
    labelKey: 'app.header.videoEditorTab',
    defaultLabel: 'Video Editor',
    testId: 'video-editor-tab',
  },
  {
    id: 'misc',
    icon: <LayoutGrid className="h-5 w-5" />,
    labelKey: 'app.header.miscellaneousTab',
    defaultLabel: 'Miscellaneous',
    testId: 'misc-tab',
  },
];

/**
 * Which child job categories roll up into each top-level tab's badge.
 * (The flat per-feature tabs are now sub-tabs, so their active-generation
 * counts aggregate onto the parent.)
 */
const BADGE_AGGREGATION: Partial<Record<AppMode, GenerationTabMode[]>> = {
  // 'scene' (Text to Video) and 'imageToVideo' are sub-tabs of the Video page,
  // so their running jobs roll up into the same badge as the AI Director's.
  video: ['video', 'scene', 'imageToVideo'],
  image: ['image'],
  audio: ['audio', 'dubbing', 'music', 'soundEffects', 'stt', 'voiceclone'],
  youtube: ['youtube', 'thumbnail'],
  misc: ['converter', 'translate'],
};

export function Sidebar({ currentMode, onModeChange }: SidebarProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const { activeCountsByTab } = useGeneration();

  const badgeCountFor = (id: AppMode): number =>
    (BADGE_AGGREGATION[id] ?? []).reduce((sum, key) => sum + (activeCountsByTab.get(key) ?? 0), 0);

  // Auto-close mobile menu when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  // Close mobile menu when clicking outside or changing mode
  const handleModeChange = (mode: AppMode) => {
    onModeChange(mode);
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMobileOpen]);

  return (
    <>
      {/* Mobile Hamburger Button - Fixed in top-left */}
      {isMobile && (
        <button
          onClick={() => setIsMobileOpen(true)}
          className="mobile-menu-button"
          data-testid="mobile-menu-button"
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobile && isMobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setIsMobileOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${
          isMobile
            ? isMobileOpen
              ? 'sidebar-mobile-open'
              : 'sidebar-mobile-closed'
            : isCollapsed
              ? 'sidebar-collapsed'
              : 'sidebar-expanded'
        }`}
        data-testid="sidebar"
      >
        {/* Mobile Close Button */}
        {isMobile && (
          <button
            onClick={() => setIsMobileOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 transition-colors md:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5 text-white/60" />
          </button>
        )}

        {/* Logo Section */}
        <div className="sidebar-header">
          <div className="flex items-center space-x-3">
            <img
              src={publicAsset('logo-96.png')}
              alt="Asgard Studio"
              className="w-10 h-10 flex-shrink-0"
            />
            {(!isCollapsed || isMobile) && (
              <div className="overflow-hidden">
                <h1 className="text-lg font-bold text-white truncate">{t('app.header.title')}</h1>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="sidebar-nav">
          <div className="space-y-1">
            {sidebarItems.map((item) => {
              const activeCount = badgeCountFor(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => handleModeChange(item.id)}
                  className={`sidebar-item ${currentMode === item.id ? 'sidebar-item-active' : ''}`}
                  data-testid={item.testId}
                  title={
                    isCollapsed && !isMobile
                      ? t(item.labelKey, { defaultValue: item.defaultLabel })
                      : undefined
                  }
                >
                  <span className="sidebar-item-icon relative">
                    {item.icon}
                    <SidebarBadge count={activeCount} />
                  </span>
                  {(!isCollapsed || isMobile) && (
                    <span className="sidebar-item-label">
                      {t(item.labelKey, { defaultValue: item.defaultLabel })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Divider */}
        <div className="sidebar-divider" />

        {/* Bottom Section - Settings (now a full page with sub-tabs incl. Installations + Help) */}
        <div className="sidebar-footer">
          <button
            className={`sidebar-item ${currentMode === 'settings' ? 'sidebar-item-active' : ''}`}
            onClick={() => handleModeChange('settings')}
            title={
              isCollapsed && !isMobile
                ? t('app.sidebar.settings', { defaultValue: 'Settings' })
                : undefined
            }
            data-testid="settings-button"
          >
            <span className="sidebar-item-icon">
              <Settings className="h-5 w-5" />
            </span>
            {(!isCollapsed || isMobile) && (
              <span className="sidebar-item-label">
                {t('app.sidebar.settings', { defaultValue: 'Settings' })}
              </span>
            )}
          </button>
        </div>

        {/* Collapse Toggle - Desktop only */}
        {!isMobile && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="sidebar-toggle"
            data-testid="sidebar-toggle"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        )}
      </aside>
    </>
  );
}
